// audioController.js - Audio playback controller
// Uses Web Audio API for gapless on-demand playback
// Standard audio element for live streaming

import { getLiveStreamUrl, getSegmentUrl, refreshLiveStreamUrl } from './api'

class AudioController {
  constructor() {
    this.audio = null
    this.previewAudio = null
    this.store = null
    this.liveDelay = 30000
    this.destroyed = false
    this.isSeeking = false
    
    // Web Audio API state
    this.audioContext = null
    this.gainNode = null
    this.currentSource = null
    this.currentBuffer = null
    this.nextBuffer = null
    this.nextSegmentId = null
    this.playbackStartTime = 0
    this.playbackOffset = 0
    this.currentSegmentId = null
    this.currentSegmentTimestamp = 0
    this.currentSegmentDuration = 0
    this.isPlaying = false
    this.isPreloading = false
    this.isTransitioning = false
    this._timeUpdateInterval = null
    this._preloadTimeout = null
    this._abortController = null
    
    // Bound event handlers for live audio
    this._onPlay = null
    this._onPause = null
    this._onError = null
  }

  get isLive() {
    return this.store ? this.store.state.isLive : true
  }

  init(store) {
    this._removeListeners()
    
    this.store = store
    this.destroyed = false
    this.audio = document.getElementById('audio-player')
    this.previewAudio = document.getElementById('audio-preview')
    
    if (!this.audio || !this.previewAudio) {
      return
    }
    
    this.audio.volume = store.state.volume / 100
    this.previewAudio.volume = store.state.volume / 100
    
    this._setupLiveEventHandlers(store)
    this._initWebAudio()
  }

  _initWebAudio() {
    if (this.audioContext) return
    
    this.audioContext = new (window.AudioContext || window.webkitAudioContext)()
    this.gainNode = this.audioContext.createGain()
    this.gainNode.connect(this.audioContext.destination)
    // Respect muted state on init
    const isMuted = this.store ? this.store.state.muted : false
    this.gainNode.gain.value = isMuted ? 0 : (this.store ? this.store.state.volume / 100 : 1)
  }

  _setupLiveEventHandlers(store) {
    this._onPlay = () => {
      store.commit('SET_PLAYING', true)
    }
    
    this._onPause = () => {
      store.commit('SET_PLAYING', false)
    }
    
    this._onError = async () => {
      if (this.destroyed) return
      
      if (this.isLive) {
        const newUrl = await refreshLiveStreamUrl()
        if (newUrl) {
          this.audio.src = newUrl
          this.audio.load()
          this.audio.play().catch(() => {})
        }
      }
    }
    
    this.audio.addEventListener('play', this._onPlay)
    this.audio.addEventListener('pause', this._onPause)
    this.audio.addEventListener('error', this._onError)
  }
  
  _removeListeners() {
    if (this.audio) {
      if (this._onPlay) this.audio.removeEventListener('play', this._onPlay)
      if (this._onPause) this.audio.removeEventListener('pause', this._onPause)
      if (this._onError) this.audio.removeEventListener('error', this._onError)
    }
  }

  // ============ Web Audio API Methods ============

  async _fetchAndDecode(segmentId) {
    // Abort any pending fetch
    if (this._abortController) {
      this._abortController.abort()
    }
    this._abortController = new AbortController()
    
    const url = getSegmentUrl(segmentId)
    const response = await fetch(url, { signal: this._abortController.signal })
    const arrayBuffer = await response.arrayBuffer()
    
    this._abortController = null
    return await this.audioContext.decodeAudioData(arrayBuffer)
  }

  _playBuffer(buffer, offset = 0, when = 0) {
    // Stop any currently playing source
    if (this.currentSource) {
      try {
        this.currentSource.onended = null
        this.currentSource.stop()
      } catch (e) {
        // Source may already be stopped
      }
    }
    
    // Resume context if suspended (browser autoplay policy)
    if (this.audioContext.state === 'suspended') {
      this.audioContext.resume()
    }
    
    // Create new source
    const source = this.audioContext.createBufferSource()
    source.buffer = buffer
    source.connect(this.gainNode)
    
    // Track playback position
    this.currentSource = source
    this.currentBuffer = buffer
    this.playbackStartTime = when || this.audioContext.currentTime
    this.playbackOffset = offset
    this.isPlaying = true
    
    // Handle segment end
    source.onended = () => {
      if (this.isPlaying && !this.isTransitioning) {
        this._onSegmentEnd()
      }
    }
    
    // Start playback
    const startTime = when || this.audioContext.currentTime
    source.start(startTime, offset)
    
    this.store.commit('SET_PLAYING', true)
    this._startTimeUpdates()
  }

  _startTimeUpdates() {
    this._stopTimeUpdates()
    
    this._timeUpdateInterval = setInterval(() => {
      if (!this.isPlaying || this.destroyed || this.isSeeking) return
      
      // Skip updates during navigation (store is controlling currentTime)
      if (this.store.state.skipActiveMetadataUpdate) return
      
      const elapsed = this.audioContext.currentTime - this.playbackStartTime
      const currentPos = this.playbackOffset + elapsed
      const timestamp = this.currentSegmentTimestamp + (currentPos * 1000)
      
      this.store.commit('SET_CURRENT_TIME', timestamp)
      
      // Update active metadata
      this._checkAndUpdateActiveMetadata(timestamp)
      
      // Pre-decode next segment when 10 seconds from end
      const timeRemaining = this.currentSegmentDuration - currentPos
      if (timeRemaining < 10 && timeRemaining > 0) {
        this._preloadNextSegment()
      }
      
      // Schedule next segment for gapless playback
      if (timeRemaining < 0.5 && timeRemaining > 0 && this.nextBuffer && !this.isTransitioning) {
        this._scheduleNextSegment(timeRemaining)
      }
    }, 50)
  }

  _stopTimeUpdates() {
    if (this._timeUpdateInterval) {
      clearInterval(this._timeUpdateInterval)
      this._timeUpdateInterval = null
    }
  }

  _clearPreloadTimeout() {
    if (this._preloadTimeout) {
      clearTimeout(this._preloadTimeout)
      this._preloadTimeout = null
    }
  }

  async _preloadNextSegment() {
    if (this.isPreloading || !this.currentSegmentId) return
    
    const segments = this.store.state.segments
    if (!segments || segments.length === 0) return
    
    const sortedSegments = [...segments].sort((a, b) => a.timestamp - b.timestamp)
    const currentIndex = sortedSegments.findIndex(s => s.id === this.currentSegmentId)
    
    if (currentIndex === -1 || currentIndex >= sortedSegments.length - 1) return
    
    const nextSeg = sortedSegments[currentIndex + 1]
    if (this.nextSegmentId === nextSeg.id) return
    
    this.isPreloading = true
    
    try {
      this.nextBuffer = await this._fetchAndDecode(nextSeg.id)
      this.nextSegmentId = nextSeg.id
    } catch (e) {
      // Fetch aborted or failed - ignore
      this.nextBuffer = null
      this.nextSegmentId = null
    }
    
    this.isPreloading = false
  }

  _scheduleNextSegment(timeRemaining) {
    if (!this.nextBuffer || this.isTransitioning) return
    
    this.isTransitioning = true
    
    const segments = this.store.state.segments
    const sortedSegments = [...segments].sort((a, b) => a.timestamp - b.timestamp)
    const currentIndex = sortedSegments.findIndex(s => s.id === this.currentSegmentId)
    const nextSeg = sortedSegments[currentIndex + 1]
    
    // Schedule next segment to start exactly when current ends
    const scheduleTime = this.audioContext.currentTime + timeRemaining
    
    // Stop current source at exact end time
    if (this.currentSource) {
      try {
        this.currentSource.onended = null
        this.currentSource.stop(scheduleTime)
      } catch (e) {
        // Source may already be stopped
      }
    }
    
    // Create and schedule next source
    const source = this.audioContext.createBufferSource()
    source.buffer = this.nextBuffer
    source.connect(this.gainNode)
    
    source.onended = () => {
      if (this.isPlaying && !this.isTransitioning) {
        this._onSegmentEnd()
      }
    }
    
    source.start(scheduleTime, 0)
    
    // Update state
    this.currentSource = source
    this.currentBuffer = this.nextBuffer
    this.currentSegmentId = nextSeg.id
    this.currentSegmentTimestamp = nextSeg.timestamp
    this.currentSegmentDuration = this.nextBuffer.duration
    this.playbackStartTime = scheduleTime
    this.playbackOffset = 0
    
    this.store.commit('SET_CURRENT_SEGMENT_ID', nextSeg.id)
    this._updateActiveMetadata(nextSeg.timestamp)
    
    // Clear preloaded buffer
    this.nextBuffer = null
    this.nextSegmentId = null
    this.isTransitioning = false
    
    // Start preloading the next-next segment
    this._clearPreloadTimeout()
    this._preloadTimeout = setTimeout(() => {
      this._preloadTimeout = null
      this._preloadNextSegment()
    }, 100)
  }

  async _onSegmentEnd() {
    const segments = this.store.state.segments
    if (!segments || segments.length === 0) return
    
    const sortedSegments = [...segments].sort((a, b) => a.timestamp - b.timestamp)
    const currentIndex = sortedSegments.findIndex(s => s.id === this.currentSegmentId)
    
    if (currentIndex === -1 || currentIndex >= sortedSegments.length - 1) {
      // No more segments - stop
      this.isPlaying = false
      this.store.commit('SET_PLAYING', false)
      this._stopTimeUpdates()
      return
    }
    
    const nextSeg = sortedSegments[currentIndex + 1]
    
    // Use pre-decoded buffer if available
    if (this.nextBuffer && this.nextSegmentId === nextSeg.id) {
      this.currentSegmentId = nextSeg.id
      this.currentSegmentTimestamp = nextSeg.timestamp
      this.currentSegmentDuration = this.nextBuffer.duration
      this.store.commit('SET_CURRENT_SEGMENT_ID', nextSeg.id)
      this._updateActiveMetadata(nextSeg.timestamp)
      
      this._playBuffer(this.nextBuffer, 0)
      
      this.nextBuffer = null
      this.nextSegmentId = null
    } else {
      // Fetch and play
      try {
        const buffer = await this._fetchAndDecode(nextSeg.id)
        this.currentSegmentId = nextSeg.id
        this.currentSegmentTimestamp = nextSeg.timestamp
        this.currentSegmentDuration = buffer.duration
        this.store.commit('SET_CURRENT_SEGMENT_ID', nextSeg.id)
        this._updateActiveMetadata(nextSeg.timestamp)
        
        this._playBuffer(buffer, 0)
      } catch (e) {
        // Fetch failed
        this.isPlaying = false
        this.store.commit('SET_PLAYING', false)
      }
    }
  }

  _stopWebAudio() {
    // Abort any pending fetch
    if (this._abortController) {
      this._abortController.abort()
      this._abortController = null
    }
    
    if (this.currentSource) {
      try {
        this.currentSource.onended = null
        this.currentSource.stop()
      } catch (e) {
        // Source may already be stopped
      }
      this.currentSource = null
    }
    this.isPlaying = false
    this.isTransitioning = false
    this._stopTimeUpdates()
    this._clearPreloadTimeout()
  }

  _updateActiveMetadata(timestamp) {
    if (!this.store) return
    if (this.store.state.skipActiveMetadataUpdate) return
    
    const metadataList = this.store.state.metadataList
    if (!metadataList || metadataList.length === 0) return
    
    const sortedMeta = [...metadataList].sort((a, b) => b.timestamp - a.timestamp)
    for (const m of sortedMeta) {
      if (timestamp >= m.timestamp) {
        if (m.id !== this.store.state.activeMetadataId) {
          this.store.commit('SET_ACTIVE_METADATA_ID', m.id)
        }
        return
      }
    }
    if (sortedMeta.length > 0) {
      this.store.commit('SET_ACTIVE_METADATA_ID', sortedMeta[sortedMeta.length - 1].id)
    }
  }

  _checkAndUpdateActiveMetadata(timestamp) {
    if (!this.store) return
    if (this.store.state.skipActiveMetadataUpdate) return
    
    const currentActiveId = this.store.state.activeMetadataId
    const metadataList = this.store.state.metadataList
    if (!metadataList || metadataList.length === 0) return
    
    const sortedMeta = [...metadataList].sort((a, b) => b.timestamp - a.timestamp)
    let expectedActiveId = null
    for (const m of sortedMeta) {
      if (timestamp >= m.timestamp) {
        expectedActiveId = m.id
        break
      }
    }
    
    if (expectedActiveId && expectedActiveId !== currentActiveId) {
      this.store.commit('SET_ACTIVE_METADATA_ID', expectedActiveId)
    }
  }

  // ============ Public API ============

  reset() {
    this._stopTimeUpdates()
    this._stopWebAudio()
    this._clearPreloadTimeout()
    
    this.isSeeking = false
    this.currentBuffer = null
    this.nextBuffer = null
    this.nextSegmentId = null
    this.currentSegmentId = null
    if (this.store) {
      this.store.commit('SET_BUFFERING', false)
    }
  }

  playLive() {
    this._stopTimeUpdates()
    this._stopWebAudio()
    this._clearPreloadTimeout()
    
    this.isSeeking = false
    this.currentBuffer = null
    this.nextBuffer = null
    this.nextSegmentId = null
    this.currentSegmentId = null
    
    this.store.commit('SET_LIVE', true)
    this.store.commit('SET_CURRENT_TIME', Date.now())
    this.store.commit('SET_CURRENT_SEGMENT_ID', null)
    this.store.commit('SET_BUFFERING', false)
    this.audio.src = getLiveStreamUrl()
    this.audio.load()
    this.audio.play().catch(() => {})
  }

  async seekTo(timestamp, metadataId = null) {
    const wasPlaying = this.store.state.isPlaying
    
    // Stop everything first to prevent race conditions
    this.isSeeking = true
    this._stopTimeUpdates()
    this._stopWebAudio()
    this._clearPreloadTimeout()
    this.audio.pause()
    
    // Clear preload state
    this.nextBuffer = null
    this.nextSegmentId = null
    
    const now = Date.now()
    if (timestamp > now) timestamp = now
    
    // Only set these if not already set by caller (e.g., gotoTimestamp)
    if (this.store.state.isLive) {
      this.store.commit('SET_LIVE', false)
    }
    this.store.commit('SET_BUFFERING', true)
    
    if (metadataId) {
      this.store.commit('SET_ACTIVE_METADATA_ID', metadataId)
    }
    
    // Check if going live
    if (timestamp >= now - this.liveDelay) {
      this.store.commit('SET_LIVE', true)
      this.store.commit('SET_CURRENT_TIME', Date.now())
      this.store.commit('SET_BUFFERING', false)
      this.isSeeking = false
      this.audio.src = getLiveStreamUrl()
      this.audio.load()
      if (wasPlaying) {
        this.audio.play().catch(() => {})
      }
      return
    }
    
    // Find segment containing timestamp
    const seg = this.store.state.segments.find(s => {
      const dur = s.actualDuration || s.duration || 60000
      return timestamp >= s.timestamp && timestamp < s.timestamp + dur
    })
    
    if (!seg) {
      this.store.commit('SET_BUFFERING', false)
      this.isSeeking = false
      return
    }
    
    this.store.commit('SET_CURRENT_SEGMENT_ID', seg.id)
    
    try {
      // Ensure Web Audio is initialized
      this._initWebAudio()
      
      // Fetch and decode segment
      const buffer = await this._fetchAndDecode(seg.id)
      
      // Check if we were destroyed during fetch
      if (this.destroyed) return
      
      // Calculate offset into segment
      const offset = (timestamp - seg.timestamp) / 1000
      const clampedOffset = Math.max(0, Math.min(offset, buffer.duration - 0.1))
      
      this.currentSegmentId = seg.id
      this.currentSegmentTimestamp = seg.timestamp
      this.currentSegmentDuration = buffer.duration
      this.currentBuffer = buffer
      this.playbackOffset = clampedOffset
      
      this.store.commit('SET_BUFFERING', false)
      this.isSeeking = false
      
      if (wasPlaying) {
        this._playBuffer(buffer, clampedOffset)
      } else {
        this.store.commit('SET_PLAYING', false)
      }
      
      // Start preloading next segment
      this._preloadNextSegment()
    } catch (e) {
      // Fetch aborted or failed
      if (!this.destroyed) {
        this.store.commit('SET_BUFFERING', false)
        this.isSeeking = false
      }
    }
  }

  toggle() {
    if (this.store.state.isPlaying) {
      if (this.isLive) {
        this.audio.pause()
      } else {
        this.pause()
      }
    } else if (this.isLive) {
      this.audio.src = getLiveStreamUrl()
      this.audio.load()
      this.audio.play().catch(() => {})
    } else if (this.store.state.viewLocked) {
      const { viewStart, viewDuration } = this.store.state
      const centerTime = viewStart + (viewDuration * 0.5)
      this.store.dispatch('gotoTimestamp', centerTime).then(() => {
        if (this.store.state.currentSegmentId && !this.store.state.isPlaying) {
          this.play()
        } else if (!this.store.state.currentSegmentId) {
          this._playNextAvailableSegment(centerTime)
        }
      })
    } else if (this.currentBuffer && this.currentSegmentId) {
      this.play()
    } else {
      const currentTime = this.store.state.currentTime
      this.store.dispatch('gotoTimestamp', currentTime).then(() => {
        if (this.store.state.currentSegmentId && !this.store.state.isPlaying) {
          this.play()
        } else if (!this.store.state.currentSegmentId) {
          this._playNextAvailableSegment(currentTime)
        }
      })
    }
  }

  _playNextAvailableSegment(timestamp) {
    const segments = this.store.state.segments
    if (!segments || segments.length === 0) {
      this.playLive()
      return
    }
    
    const sortedSegments = [...segments].sort((a, b) => a.timestamp - b.timestamp)
    const nextSegment = sortedSegments.find(s => s.timestamp > timestamp)
    
    if (nextSegment) {
      this.seekTo(nextSegment.timestamp)
    } else {
      this.playLive()
    }
  }

  play() {
    if (this.isLive) {
      this.audio.play().catch(() => {})
    } else {
      if (this.audioContext && this.audioContext.state === 'suspended') {
        this.audioContext.resume()
      }
      
      if (this.currentBuffer && !this.isPlaying) {
        this._playBuffer(this.currentBuffer, this.playbackOffset)
      }
    }
  }

  pause() {
    if (this.isLive) {
      this.audio.pause()
    } else {
      if (this.currentSource && this.isPlaying) {
        const elapsed = this.audioContext.currentTime - this.playbackStartTime
        this.playbackOffset = this.playbackOffset + elapsed
        
        try {
          this.currentSource.onended = null
          this.currentSource.stop()
        } catch (e) {
          // Source may already be stopped
        }
        this.currentSource = null
      }
      this.isPlaying = false
      this._stopTimeUpdates()
      this.store.commit('SET_PLAYING', false)
    }
  }

  skip(seconds) {
    if (this.isLive && seconds < 0) {
      this.store.dispatch('gotoTimestamp', Date.now() + (seconds * 1000))
    } else if (!this.isLive) {
      this.store.dispatch('gotoTimestamp', this.store.state.currentTime + (seconds * 1000))
    }
  }

  setVolume(volume) {
    const v = volume / 100
    this.store.commit('SET_VOLUME', volume)
    // Unmute if muted and volume is changed
    if (this.store.state.muted) {
      this.store.commit('SET_MUTED', false)
      this.audio.muted = false
      this.previewAudio.muted = false
    }
    this.audio.volume = v
    this.previewAudio.volume = v
    if (this.gainNode) {
      this.gainNode.gain.value = v
    }
  }

  toggleMute() {
    const newMuted = !this.store.state.muted
    this.store.commit('SET_MUTED', newMuted)
    this.audio.muted = newMuted
    this.previewAudio.muted = newMuted
    if (this.gainNode) {
      this.gainNode.gain.value = newMuted ? 0 : (this.store.state.volume / 100)
    }
  }

  setMuted(muted) {
    this.store.commit('SET_MUTED', muted)
    this.audio.muted = muted
    this.previewAudio.muted = muted
    if (this.gainNode) {
      this.gainNode.gain.value = muted ? 0 : (this.store.state.volume / 100)
    }
  }

  checkAndUpdateActiveMetadata(timestamp) {
    this._checkAndUpdateActiveMetadata(timestamp)
  }

  updateActiveMetadata(timestamp) {
    this._updateActiveMetadata(timestamp)
  }

  previewStart(duration = 3000) {
    const sel = this.store.getters.selectionNormalized
    if (!sel) return
    this.stopPreview()
    
    const segment = this.store.state.segments.find(s => {
      const dur = s.actualDuration || s.duration || 60000
      return sel.start >= s.timestamp && sel.start < s.timestamp + dur
    })
    if (!segment) return
    
    this.store.commit('SET_PREVIEW', { type: 'start' })
    this.previewAudio.src = getSegmentUrl(segment.id)
    const offset = (sel.start - segment.timestamp) / 1000
    this.previewAudio.load()
    
    this.previewAudio.onloadedmetadata = () => {
      if (offset > 0 && offset < this.previewAudio.duration) {
        this.previewAudio.currentTime = offset
      }
      this.previewAudio.play().catch(() => {})
      this.previewAudio.onloadedmetadata = null
    }
    
    const timeout = setTimeout(() => this.stopPreview(), duration)
    this.store.commit('SET_PREVIEW', { timeout })
  }

  previewEnd(duration = 3000) {
    const sel = this.store.getters.selectionNormalized
    if (!sel) return
    this.stopPreview()
    
    const previewStartTime = sel.end - duration
    const segment = this.store.state.segments.find(s => {
      const dur = s.actualDuration || s.duration || 60000
      return previewStartTime >= s.timestamp && previewStartTime < s.timestamp + dur
    })
    if (!segment) return
    
    this.store.commit('SET_PREVIEW', { type: 'end' })
    this.previewAudio.src = getSegmentUrl(segment.id)
    const offset = (previewStartTime - segment.timestamp) / 1000
    this.previewAudio.load()
    
    this.previewAudio.onloadedmetadata = () => {
      if (offset > 0 && offset < this.previewAudio.duration) {
        this.previewAudio.currentTime = offset
      }
      this.previewAudio.play().catch(() => {})
      this.previewAudio.onloadedmetadata = null
    }
    
    const timeout = setTimeout(() => this.stopPreview(), duration)
    this.store.commit('SET_PREVIEW', { timeout })
  }

  stopPreview() {
    if (this.store.state.previewTimeout) {
      clearTimeout(this.store.state.previewTimeout)
    }
    this.previewAudio.pause()
    this.previewAudio.src = ''
    this.store.commit('SET_PREVIEW', { type: null, timeout: null })
  }

  goLive() {
    this.store.dispatch('goLive')
  }

  destroy() {
    this.destroyed = true
    this._removeListeners()
    this._stopWebAudio()
    this._clearPreloadTimeout()
    
    // Abort any pending fetch
    if (this._abortController) {
      this._abortController.abort()
      this._abortController = null
    }
    
    this.currentBuffer = null
    this.nextBuffer = null
    this.nextSegmentId = null
    this.currentSegmentId = null
    
    if (this.audio) {
      this.audio.pause()
      this.audio.src = ''
    }
    if (this.previewAudio) {
      this.previewAudio.pause()
      this.previewAudio.src = ''
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {})
      this.audioContext = null
      this.gainNode = null
    }
    this.store = null
  }
}

export default new AudioController()
